
import React, { useCallback, useEffect, useMemo, useState } from "react";

const API_BASE =
  process.env.REACT_APP_API_URL || "https://bmtvendorvendorfinal.onrender.com";

const API_URL = `${API_BASE}/api/apartment-bookings`;

const TABS = [
  { key: "all", label: "All Bookings" },
  { key: "upcoming", label: "Upcoming" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];

const getToken = () => {
  const keys = ["token", "vendorToken", "accessToken", "vendor_token"];

  for (const key of keys) {
    const value = localStorage.getItem(key);
    if (value) return value;
  }

  return "";
};

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const money = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN")}`;

const getApartmentName = (booking) => {
  const apartment = booking.apartment;

  if (!apartment) return "Apartment";
  if (typeof apartment === "string") return "Apartment";

  return (
    apartment.apartmentName ||
    apartment.name ||
    apartment.title ||
    "Apartment"
  );
};

const getGuestName = (guest) =>
  [guest?.firstName, guest?.lastName].filter(Boolean).join(" ") ||
  "Guest";

const statusClass = (status) =>
  `apt-status apt-status-${String(status || "pending").toLowerCase()}`;

export default function Vendorapartmentbookings() {
  const [activeTab, setActiveTab] = useState("all");
  const [bookings, setBookings] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatingId, setUpdatingId] = useState("");

  const request = useCallback(async (url, options = {}) => {
    const token = getToken();

    if (!token) {
      throw new Error(
        "Vendor login token nahi mila. Please logout karke dobara login karo."
      );
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });

    const result = await response.json().catch(() => ({}));

    if (!response.ok || result.success === false) {
      throw new Error(result.message || "Request failed.");
    }

    return result;
  }, []);

  const loadBookings = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const result = await request(
        `${API_URL}/vendor/my-bookings?page=1&limit=100`
      );

      setBookings(Array.isArray(result.data) ? result.data : []);
    } catch (err) {
      setError(err.message || "Bookings load nahi hui.");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const categorized = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const upcoming = [];
    const completed = [];
    const cancelled = [];

    bookings.forEach((booking) => {
      const status = String(booking.bookingStatus || "PENDING").toUpperCase();
      const checkIn = new Date(booking.checkInDate);
      checkIn.setHours(0, 0, 0, 0);

      if (status === "CANCELLED") {
        cancelled.push(booking);
      } else if (status === "COMPLETED") {
        completed.push(booking);
      } else if (
        status !== "NO_SHOW" &&
        !Number.isNaN(checkIn.getTime()) &&
        checkIn >= today
      ) {
        upcoming.push(booking);
      }
    });

    return {
      all: bookings,
      upcoming,
      completed,
      cancelled,
    };
  }, [bookings]);

  const visibleBookings = useMemo(() => {
    const list = categorized[activeTab] || [];

    const query = search.trim().toLowerCase();
    if (!query) return list;

    return list.filter((booking) => {
      const text = [
        booking.bookingId,
        getApartmentName(booking),
        getGuestName(booking.guest),
        booking.guest?.phone,
        booking.guest?.email,
        booking.bookingStatus,
        booking.paymentStatus,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return text.includes(query);
    });
  }, [categorized, activeTab, search]);

  const updateStatus = async (booking, status) => {
    let reason = "";

    if (status === "CANCELLED") {
      const confirmed = window.confirm(
        `Booking ${booking.bookingId} cancel karni hai?`
      );

      if (!confirmed) return;

      reason = window.prompt("Cancellation reason (optional):") || "";
    } else {
      const confirmed = window.confirm(
        `Booking status ${status} karna hai?`
      );

      if (!confirmed) return;
    }

    setUpdatingId(booking._id);
    setError("");

    try {
      await request(`${API_URL}/vendor/${booking._id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status, reason }),
      });

      await loadBookings();
    } catch (err) {
      setError(err.message || "Status update nahi hua.");
    } finally {
      setUpdatingId("");
    }
  };

  const stats = [
    { label: "All Bookings", value: categorized.all.length },
    { label: "Upcoming", value: categorized.upcoming.length },
    { label: "Completed", value: categorized.completed.length },
    { label: "Cancelled", value: categorized.cancelled.length },
  ];

  return (
    <div className="apt-bookings-page">
      <style>{`
        .apt-bookings-page {
          padding: 24px;
          color: #172033;
          background: #f7f8fc;
          min-height: 100%;
          font-family: Inter, Arial, sans-serif;
        }
        .apt-bookings-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
          margin-bottom: 22px;
        }
        .apt-bookings-header h1 {
          margin: 0 0 7px;
          font-size: 26px;
          font-weight: 750;
        }
        .apt-bookings-header p {
          margin: 0;
          color: #697386;
          font-size: 14px;
        }
        .apt-refresh {
          border: 1px solid #d9deea;
          background: white;
          color: #27334a;
          padding: 10px 15px;
          border-radius: 10px;
          cursor: pointer;
          font-weight: 650;
        }
        .apt-stats {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 14px;
          margin-bottom: 22px;
        }
        .apt-stat {
          background: white;
          border: 1px solid #e8eaf1;
          border-radius: 14px;
          padding: 18px;
        }
        .apt-stat-label {
          font-size: 13px;
          color: #727c90;
          margin-bottom: 10px;
        }
        .apt-stat-value {
          font-size: 27px;
          font-weight: 750;
        }
        .apt-bookings-panel {
          background: white;
          border: 1px solid #e8eaf1;
          border-radius: 15px;
          overflow: hidden;
        }
        .apt-tabs {
          display: flex;
          gap: 6px;
          padding: 14px 16px 0;
          overflow-x: auto;
          border-bottom: 1px solid #edf0f5;
        }
        .apt-tab {
          white-space: nowrap;
          padding: 12px 14px;
          border: 0;
          border-bottom: 3px solid transparent;
          background: transparent;
          color: #667085;
          cursor: pointer;
          font-weight: 650;
        }
        .apt-tab.active {
          color: #4f46e5;
          border-bottom-color: #4f46e5;
        }
        .apt-tab-count {
          display: inline-flex;
          margin-left: 7px;
          padding: 2px 7px;
          background: #f0f1f7;
          border-radius: 20px;
          font-size: 11px;
        }
        .apt-toolbar {
          display: flex;
          gap: 12px;
          padding: 16px;
        }
        .apt-search {
          width: 100%;
          max-width: 420px;
          padding: 12px 14px;
          border: 1px solid #dfe3ec;
          border-radius: 10px;
          outline: none;
          font-size: 14px;
        }
        .apt-search:focus {
          border-color: #8179f5;
        }
        .apt-booking-list {
          padding: 0 16px 16px;
          display: grid;
          gap: 14px;
        }
        .apt-booking-card {
          border: 1px solid #e7eaf1;
          border-radius: 13px;
          padding: 17px;
          min-width: 0;
        }
        .apt-booking-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 15px;
        }
        .apt-booking-id {
          color: #667085;
          font-size: 12px;
          margin-bottom: 5px;
        }
        .apt-booking-title {
          font-size: 17px;
          font-weight: 750;
          overflow-wrap: anywhere;
        }
        .apt-status {
          display: inline-block;
          border-radius: 20px;
          padding: 6px 10px;
          font-size: 11px;
          font-weight: 750;
          background: #f1f3f7;
          color: #515b6e;
        }
        .apt-status-confirmed, .apt-status-paid {
          color: #147847;
          background: #e6f8ee;
        }
        .apt-status-pending {
          color: #9a6700;
          background: #fff5d6;
        }
        .apt-status-cancelled, .apt-status-failed {
          color: #b42318;
          background: #ffebe9;
        }
        .apt-status-completed {
          color: #4338ca;
          background: #eeedff;
        }
        .apt-booking-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 15px;
          padding: 15px 0;
          border-top: 1px solid #eff1f5;
          border-bottom: 1px solid #eff1f5;
        }
        .apt-info-label {
          color: #7b8496;
          font-size: 12px;
          margin-bottom: 6px;
        }
        .apt-info-value {
          font-size: 13px;
          font-weight: 650;
          overflow-wrap: anywhere;
        }
        .apt-booking-bottom {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 14px;
          flex-wrap: wrap;
          padding-top: 14px;
        }
        .apt-total-label {
          color: #727c90;
          font-size: 12px;
          margin-bottom: 4px;
        }
        .apt-total {
          font-size: 22px;
          font-weight: 800;
        }
        .apt-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .apt-action {
          border: 0;
          border-radius: 8px;
          padding: 9px 12px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
        }
        .apt-action:disabled {
          opacity: .5;
          cursor: not-allowed;
        }
        .apt-confirm {
          background: #e8f8ee;
          color: #137547;
        }
        .apt-complete {
          background: #eeedff;
          color: #4338ca;
        }
        .apt-cancel {
          background: #fff0ef;
          color: #b42318;
        }
        .apt-error {
          margin: 16px;
          padding: 12px 14px;
          border-radius: 9px;
          background: #fff0ef;
          color: #b42318;
          font-size: 13px;
          overflow-wrap: anywhere;
        }
        .apt-empty {
          padding: 42px 16px;
          text-align: center;
          color: #70798b;
        }
        .apt-empty strong {
          display: block;
          color: #283247;
          margin-bottom: 7px;
        }
        @media (max-width: 900px) {
          .apt-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .apt-booking-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 540px) {
          .apt-bookings-page { padding: 13px; }
          .apt-bookings-header h1 { font-size: 22px; }
          .apt-stats { gap: 9px; }
          .apt-stat { padding: 13px; }
          .apt-stat-value { font-size: 23px; }
          .apt-booking-grid { gap: 14px 10px; }
          .apt-toolbar { padding: 12px; }
          .apt-booking-list { padding: 0 10px 12px; }
          .apt-booking-card { padding: 13px; }
        }
      `}</style>

      <div className="apt-bookings-header">
        <div>
          <h1>Apartment Bookings</h1>
          <p>Manage guest reservations, stay dates and booking status.</p>
        </div>

        <button
          className="apt-refresh"
          onClick={loadBookings}
          disabled={loading}
        >
          {loading ? "Loading..." : "↻ Refresh"}
        </button>
      </div>

      <div className="apt-stats">
        {stats.map((stat) => (
          <div className="apt-stat" key={stat.label}>
            <div className="apt-stat-label">{stat.label}</div>
            <div className="apt-stat-value">{stat.value}</div>
          </div>
        ))}
      </div>

      <div className="apt-bookings-panel">
        <div className="apt-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`apt-tab ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              <span className="apt-tab-count">
                {categorized[tab.key].length}
              </span>
            </button>
          ))}
        </div>

        <div className="apt-toolbar">
          <input
            className="apt-search"
            type="search"
            placeholder="Search booking ID, guest, phone or apartment..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        {error && (
          <div className="apt-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="apt-empty">Loading apartment bookings...</div>
        ) : visibleBookings.length === 0 ? (
          <div className="apt-empty">
            <strong>No bookings found</strong>
            There are no bookings in this section yet.
          </div>
        ) : (
          <div className="apt-booking-list">
            {visibleBookings.map((booking) => {
              const bookingStatus = String(
                booking.bookingStatus || "PENDING"
              ).toUpperCase();

              const paymentStatus = String(
                booking.paymentStatus || "PENDING"
              ).toUpperCase();

              const canManage = ![
                "CANCELLED",
                "COMPLETED",
                "NO_SHOW",
              ].includes(bookingStatus);

              return (
                <article
                  className="apt-booking-card"
                  key={booking._id}
                >
                  <div className="apt-booking-top">
                    <div>
                      <div className="apt-booking-id">
                        Booking ID: {booking.bookingId || booking._id}
                      </div>
                      <div className="apt-booking-title">
                        {getApartmentName(booking)}
                      </div>
                    </div>

                    <div className="apt-actions">
                      <span className={statusClass(bookingStatus)}>
                        {bookingStatus}
                      </span>
                      <span className={statusClass(paymentStatus)}>
                        Payment: {paymentStatus}
                      </span>
                    </div>
                  </div>

                  <div className="apt-booking-grid">
                    <div>
                      <div className="apt-info-label">Guest</div>
                      <div className="apt-info-value">
                        {getGuestName(booking.guest)}
                      </div>
                    </div>

                    <div>
                      <div className="apt-info-label">Phone</div>
                      <div className="apt-info-value">
                        {booking.guest?.phone || "—"}
                      </div>
                    </div>

                    <div>
                      <div className="apt-info-label">Check-in</div>
                      <div className="apt-info-value">
                        {formatDate(booking.checkInDate)}
                      </div>
                    </div>

                    <div>
                      <div className="apt-info-label">Check-out</div>
                      <div className="apt-info-value">
                        {formatDate(booking.checkOutDate)}
                      </div>
                    </div>

                    <div>
                      <div className="apt-info-label">Rate Plan</div>
                      <div className="apt-info-value">
                        {typeof booking.ratePlan === "object"
                          ? booking.ratePlan?.name || booking.ratePlan?.code || "—"
                          : "—"}
                      </div>
                    </div>

                    <div>
                      <div className="apt-info-label">Guests</div>
                      <div className="apt-info-value">
                        {Number(booking.adults || 0)} adults,{" "}
                        {Number(booking.children || 0)} children
                      </div>
                    </div>

                    <div>
                      <div className="apt-info-label">Duration</div>
                      <div className="apt-info-value">
                        {booking.nights || 0} night(s)
                      </div>
                    </div>

                    <div>
                      <div className="apt-info-label">Payment Method</div>
                      <div className="apt-info-value">
                        {booking.paymentMethod || "—"}
                      </div>
                    </div>
                  </div>

                  <div className="apt-booking-bottom">
                    <div>
                      <div className="apt-total-label">Total Amount</div>
                      <div className="apt-total">
                        {money(booking.pricing?.totalAmount)}
                      </div>
                    </div>

                    <div className="apt-actions">
                      {canManage && bookingStatus === "PENDING" && (
                        <button
                          className="apt-action apt-confirm"
                          disabled={updatingId === booking._id}
                          onClick={() => updateStatus(booking, "CONFIRMED")}
                        >
                          Confirm
                        </button>
                      )}

                      {canManage && (
                        <button
                          className="apt-action apt-complete"
                          disabled={updatingId === booking._id}
                          onClick={() => updateStatus(booking, "COMPLETED")}
                          title="Only allowed after checkout date"
                        >
                          Complete
                        </button>
                      )}

                      {canManage && (
                        <button
                          className="apt-action apt-cancel"
                          disabled={updatingId === booking._id}
                          onClick={() => updateStatus(booking, "CANCELLED")}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>

                  {booking.guest?.email && (
                    <div style={{ marginTop: 12, color: "#727c90", fontSize: 12 }}>
                      Email: {booking.guest.email}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
